import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Divider from '@amzn/meridian/divider';
import Loader from '@amzn/meridian/loader';
import { Field, Form, FormikProps } from 'formik';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ContactDetailsInput,
  GoodRecommendationOptionsObject,
} from '../../../../api/CoachingNotes/schema';
import {
  getGoodRecommendationOptions,
  getInaccurateContactRecommendationReasonsOptions,
} from '../Data';
import Select from '../../../CoachingForm/FieldComponents/Select';
import RadioButtonGroup from '../../../CoachingForm/FieldComponents/RadioButtonGroup';
import Textarea from '../../../CoachingForm/FieldComponents/Textarea';
import { OUTLIER_INGRESS } from '../../../FormLibrary/utils/helper';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';

type FormContentProps = FormikProps<ContactDetailsInput> & {
  contactId: string;
  userId: string;
  contactCategory?: string;
  ingress?: string | null;
};

export const ContactReviewFeedbackModalFormContent: React.FC<
  FormContentProps
> = ({
  values,
  errors,
  touched,
  handleSubmit,
  contactId,
  userId,
  contactCategory,
  ingress,
}: FormContentProps): React.ReactElement => {
  const intl = useIntl();
  const [isSubmitProcessing, setSubmitProcessing] = useState(false);
  const handleFeedbackSubmitted = async (
    feedbackDetails: React.FormEvent<HTMLFormElement>
  ): Promise<any> => {
    setSubmitProcessing(true);
    handleSubmit(feedbackDetails);
  };

  return (
    <Form>
      <Column spacingInset='300 none' spacing='400'>
        <Text>
          {ingress === OUTLIER_INGRESS &&
          isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) &&
          values.goodRecommendation === GoodRecommendationOptionsObject.NO
            ? intl.formatMessage({
                id: 'cs_perfportal_contact_recom_label_title_outlier',
                defaultMessage:
                  "Tell us why this isn't a valid outlier contact to review. We'll send this to your OM for additional review.",
              })
            : intl.formatMessage({
                id: 'cs_perfportal_contact_recom_label_title',
                defaultMessage:
                  'Tell us how we are doing. Your feedback will improve our conversation recommendation logic.',
              })}
        </Text>
        <Divider />
        <Field
          as={RadioButtonGroup}
          name='goodRecommendation'
          data-cy='coaching-form-radio-button-group-field-good-recommendation'
          selectedValue={GoodRecommendationOptionsObject.YES}
          options={getGoodRecommendationOptions(intl)}
          label={intl.formatMessage({
            id: 'cs_perfportal_contact_recom_label_required_good_recom_question',
            defaultMessage:
              'Is the contact a good recommendation for coaching? *',
          })}
          errorText={touched.goodRecommendation && errors.goodRecommendation}
        />
        {values.goodRecommendation === GoodRecommendationOptionsObject.NO && (
          <Field
            as={Select}
            name='uncoachableReason'
            data-cy='select-uncoachable-reason'
            options={getInaccurateContactRecommendationReasonsOptions(intl)}
            label={intl.formatMessage({
              id: 'cs_perfportal_contact_recom_label_optional_select_uncoachable_why_not_accurate',
              defaultMessage: 'Why was it not accurate?',
            })}
            errorText={touched.uncoachableReason && errors.uncoachableReason}
          />
        )}
        {values.goodRecommendation === GoodRecommendationOptionsObject.NO &&
          ingress === OUTLIER_INGRESS &&
          isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) && (
            <Field
              as={Textarea}
              name='justification'
              data-cy='textarea-justification'
              label={intl.formatMessage({
                id: 'cs_perfportal_outlier_justification_label',
                defaultMessage: 'Justification (Optional)',
              })}
              placeholder={intl.formatMessage({
                id: 'cs_perfportal_outlier_justification_placeholder',
                defaultMessage:
                  'Please provide additional context for why this recommendation is not valid...',
              })}
              maxLength={1000}
              rows={4}
              errorText={touched.justification && errors.justification}
            />
          )}
        <Divider />
        {
          <Row alignmentHorizontal='end' spacingInset='400 none'>
            <Button
              data-csa-c-type='button'
              data-csa-c-slot-id='contact-feedback-modal-submit-button-slot'
              data-csa-c-content-id='contact-feeback-modal-submit-button'
              data-csa-c-tag1='Team'
              data-csa-c-tag2='Contact Table'
              data-csa-c-tag3='Contact Review'
              data-csa-c-tag4='Contact Feedback'
              data-csa-c-contactid={contactId}
              data-csa-c-profileId={userId}
              data-csa-c-category={contactCategory}
              data-cy='contact-feedback-modal-submit-button'
              type='primary'
              size='small'
              minWidth='70px'
              onClick={handleFeedbackSubmitted}
              disabled={isSubmitProcessing || !values.goodRecommendation}
            >
              {isSubmitProcessing ? (
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='small' />
                </div>
              ) : (
                <FormattedMessage
                  id='cs_perfportal_submit'
                  defaultMessage='Submit'
                />
              )}
            </Button>
          </Row>
        }
      </Column>
    </Form>
  );
};

export default ContactReviewFeedbackModalFormContent;
